import { useState, useEffect, useMemo } from 'react';
import {
  Alert, Box, Button, ButtonGroup, Card, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, InputAdornment, MenuItem,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import CalculateIcon from '@mui/icons-material/Calculate';
import EditIcon from '@mui/icons-material/Edit';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CurrencyInput from '../../../components/shared/CurrencyInput';
import loansApi from '../../../services/loansApi';
import brokerCompanyApi from '../../../services/brokerCompanyApi';
import brokerCompensationApi from '../../../services/brokerCompensationApi';
import { authService } from '../../../services/authService';
import FileUploadModal from '../../../components/shared/FileUploadModal';
import { Roles } from '../../../constants';
import { getAxiosErrorMessage } from '../../../utils/getAxiosErrorMessage';
import {
  getPricingLoanTermOptions,
  isNonQmOnlyProductFamilies,
  mergeTermsForNonQmPricing,
  productFamiliesIncludeNonQm,
} from '../../../services/pricingEngineService';
import MismoAdvisorStep from './MismoAdvisorStep';

const LOAN_TYPES = {
  MORTGAGE: 'Mortgage',
  HELOC: 'HELOC',
  HOME_EQUITY_LOAN: 'HomeEquityLoan',
};

const LOCK_DURATIONS = [15, 30, 45];
const PRODUCT_FAMILIES = [
  { id: 'Conventional', label: 'Conventional' },
  { id: 'FHA', label: 'FHA' },
  { id: 'VA', label: 'VA' },
  { id: 'USDA', label: 'USDA' },
  { id: 'Non-QM', label: 'Non-QM' },
];
const NewLoanModal = ({ show, onClose, onLoanCreated, mismoImportAdvisorEnabled = false }) => {
  const [importMismo] = loansApi.endpoints.importMismo.useMutation();
  const [acceptMismoAdvisor] = loansApi.endpoints.acceptMismoAdvisor.useMutation();

  // ── Active compensation query (hooks must be at top, before any early returns) ──
  const { data: brokerCompany } = brokerCompanyApi.endpoints.getMyCompany.useQuery();
  const { data: activeComp } = brokerCompensationApi.endpoints.getActiveCompensation.useQuery(
    brokerCompany?.id,
    { skip: !brokerCompany?.id }
  );

  // ── Current user role ──
  const currentUser = authService.getCurrentUser();
  const isProcessor = currentUser?.role === Roles.Processor;
  const isBroker = currentUser?.role === Roles.Broker || currentUser?.role === Roles.BrokerAdmin;

  // ── Company users for broker/processor dropdowns ──
  const { data: companyUsers = [] } = brokerCompanyApi.endpoints.getMyCompanyUsers.useQuery(
    undefined,
    { skip: !isProcessor && !isBroker }
  );
  const allBrokers = useMemo(() =>
    companyUsers.filter(u => (u.role === Roles.Broker || u.role === Roles.BrokerAdmin) && !u.inactive),
    [companyUsers]
  );
  const processors = useMemo(() =>
    companyUsers.filter(u => u.role === Roles.Processor && !u.inactive),
    [companyUsers]
  );

  // step: upload -> confirm -> filters -> advisor
  const [step, setStep] = useState('upload');
  const [selectedFile, setSelectedFile] = useState(null);
  const [importLoanType, setImportLoanType] = useState(LOAN_TYPES.MORTGAGE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [postAction, setPostAction] = useState('price');
  const [lockDuration, setLockDuration] = useState(30);
  const [compensationType, setCompensationType] = useState('LPC');
  const [selectedFamilies, setSelectedFamilies] = useState(['Conventional']);
  const [selectedTerms, setSelectedTerms] = useState([30]);
  const [bpcCalcMethod, setBpcCalcMethod] = useState('PercentOfLoan');
  const [bpcValue, setBpcValue] = useState('');

  // Broker/Processor selection
  const [selectedBrokerUserId, setSelectedBrokerUserId] = useState('');
  const [selectedProcessorUserId, setSelectedProcessorUserId] = useState('');

  // Auto-select processor if only one exists
  useEffect(() => {
    if (isBroker && processors.length === 1) {
      setSelectedProcessorUserId(processors[0].userId);
    }
  }, [isBroker, processors]);

  const loanTermOptions = useMemo(
    () => getPricingLoanTermOptions(selectedFamilies),
    [selectedFamilies],
  );

  const applyNonQmMortgageType = isNonQmOnlyProductFamilies(selectedFamilies);

  useEffect(() => {
    if (!productFamiliesIncludeNonQm(selectedFamilies)) {
      setSelectedTerms((prev) => {
        const next = prev.filter((t) => t !== 40);
        return next.length > 0 ? next : [30];
      });
    }
  }, [selectedFamilies]);

  const ModalTitleWithClose = ({ children }) => (
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
      <span>{children}</span>
      <IconButton aria-label="Close" onClick={handleClose} size="small" sx={{ color: 'text.secondary' }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
  );

  const handleClose = () => {
    setStep('upload');
    setSelectedFile(null);
    setImportLoanType(LOAN_TYPES.MORTGAGE);
    setError(null);
    setLoading(false);
    setPostAction('price');
    setLockDuration(30);
    setCompensationType('LPC');
    setSelectedFamilies(['Conventional']);
    setSelectedTerms([30]);
    setBpcCalcMethod('PercentOfLoan');
    setBpcValue('');
    setSelectedBrokerUserId('');
    setSelectedProcessorUserId('');
    onClose();
  };

  const handleFileConfirm = (file) => {
    setSelectedFile(file);
    setStep('confirm');
  };

  const handleLoanTypeNext = () => {
    if (!importLoanType) {
      setError('Please select a loan type');
      return;
    }
    setError(null);
    setStep(mismoImportAdvisorEnabled ? 'filters' : 'action');
  };

  const handleImportSubmit = async () => {
    if (isProcessor && !selectedBrokerUserId) {
      setError('Please select a broker for the loan');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await importLoan();
      setLoading(false);
      const loanInfo = { id: result.loanId, loanNumber: result.loanNumber };
      const pricingTerms = mergeTermsForNonQmPricing(selectedTerms, selectedFamilies);
      if (postAction === 'price') {
        onLoanCreated({
          ...loanInfo,
          action: 'price',
          pricingFilters: {
            lockDuration,
            compensationType,
            selectedFamilies,
            selectedTerms: pricingTerms,
            applyNonQmMortgageType,
            ...(compensationType === 'BPC' ? { bpcCalcMethod, bpcValue } : {}),
          },
        });
      } else {
        onLoanCreated({
          ...loanInfo,
          action: 'edit',
          pricingFilters: applyNonQmMortgageType ? { applyNonQmMortgageType: true } : undefined,
        });
      }
      handleClose();
    } catch (err) {
      setLoading(false);
      setError(getAxiosErrorMessage(err, 'Failed to import MISMO file'));
    }
  };

  const buildPricingFilters = () => ({
    lockDuration,
    compensationType,
    selectedFamilies,
    selectedTerms: mergeTermsForNonQmPricing(selectedTerms, selectedFamilies),
    applyNonQmMortgageType: isNonQmOnlyProductFamilies(selectedFamilies),
    ...(compensationType === 'BPC' ? { bpcCalcMethod, bpcValue } : {}),
  });

  const importLoan = async () => {
    const importPayload = {
      file: selectedFile,
      loanType: importLoanType,
      ...(selectedBrokerUserId && { brokerUserId: selectedBrokerUserId }),
      ...(selectedProcessorUserId && { processorUserId: selectedProcessorUserId }),
    };
    return importMismo(importPayload).unwrap();
  };

  const handleAdvisorAgree = async ({ assignPayload }) => {
    if (isProcessor && !selectedBrokerUserId) {
      setError('Please select a broker for the loan');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const imported = await importLoan();
      const loanId = imported.loanId;
      await acceptMismoAdvisor({
        loanId,
        pricingRequest: assignPayload.pricingRequest,
        productId: assignPayload.productId,
        rate: assignPayload.rate,
        price: assignPayload.price,
        lockTerm: assignPayload.lockTerm,
        ...(assignPayload.quoteId ? { quoteId: assignPayload.quoteId } : {}),
        ...(assignPayload.bpcCalcMethod
          ? { bpcCalcMethod: assignPayload.bpcCalcMethod, bpcValue: assignPayload.bpcValue }
          : {}),
      }).unwrap();
      const loanInfo = {
        id: loanId,
        loanNumber: imported.loanNumber,
        action: 'advisor-complete',
        pricingFilters: buildPricingFilters(),
      };
      handleClose();
      onLoanCreated(loanInfo);
    } catch (err) {
      setLoading(false);
      setError(getAxiosErrorMessage(err, 'Failed to import and assign recommended product.'));
    }
  };

  const handleAdvisorDisagree = async ({ pricingFilters, preferredProductShortName, preferredRate, advisorOptions }) => {
    if (isProcessor && !selectedBrokerUserId) {
      setError('Please select a broker for the loan');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const imported = await importLoan();
      setLoading(false);
      onLoanCreated({
        id: imported.loanId,
        loanNumber: imported.loanNumber,
        action: 'price',
        pricingFilters: {
          ...pricingFilters,
          preferredProductShortName,
          preferredRate,
          advisorOptions,
        },
      });
      handleClose();
    } catch (err) {
      setLoading(false);
      setError(getAxiosErrorMessage(err, 'Failed to import MISMO file.'));
    }
  };

  const toggleFilter = (arr, setArr, val) => {
    setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  if (!show) return null;

  // ── File chip (reused in steps 2 & 3) ──
  const fileChip = (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2, mb: 2 }}>
      <InsertDriveFileIcon color="primary" sx={{ fontSize: 32 }} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={700}>{selectedFile?.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {selectedFile?.size > 1048576
            ? `${(selectedFile.size / 1048576).toFixed(1)} MB`
            : `${(selectedFile?.size / 1024).toFixed(0)} KB`}
        </Typography>
      </Box>
      <Tooltip title="Change file">
        <IconButton size="small" onClick={() => setStep('upload')}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  // Step 1: File selection
  if (step === 'upload') {
    return (
      <FileUploadModal
        show
        onClose={handleClose}
        onConfirm={handleFileConfirm}
        title="New Loan — Select MISMO 3.4 XML"
        confirmLabel="Continue"
        accept=".xml"
        multiple={false}
        hint="MISMO 3.4 XML files only"
      />
    );
  }

  // Step 2: Loan type selection
  if (step === 'confirm') {
    return (
      <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalTitleWithClose>New Loan — Loan Type</ModalTitleWithClose>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {fileChip}
          <TextField
            select
            label="Loan Type"
            required
            fullWidth
            size="small"
            value={importLoanType}
            onChange={(e) => setImportLoanType(e.target.value)}
            helperText="This cannot be inferred from the XML and must be specified"
            sx={{ mb: 2 }}
          >
            <MenuItem value={LOAN_TYPES.MORTGAGE}>Mortgage</MenuItem>
            <MenuItem value={LOAN_TYPES.HELOC} disabled>HELOC — Coming Soon</MenuItem>
            <MenuItem value={LOAN_TYPES.HOME_EQUITY_LOAN} disabled>Home Equity Loan — Coming Soon</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" startIcon={<ArrowBackIcon />} onClick={() => setStep('upload')}>Back</Button>
          <Button variant="contained" endIcon={<ArrowForwardIcon />} disabled={!importLoanType} onClick={handleLoanTypeNext}>Continue</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (step === 'action') {
    return (
      <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalTitleWithClose>New Loan — What&apos;s Next?</ModalTitleWithClose>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {fileChip}
          {isProcessor && (
            <TextField
              select
              label="Select Broker"
              required
              fullWidth
              size="small"
              value={selectedBrokerUserId}
              onChange={(e) => setSelectedBrokerUserId(e.target.value)}
              helperText="The broker who will originate this loan"
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Select a broker...</MenuItem>
              {allBrokers.map(b => (
                <MenuItem key={b.userId} value={b.userId}>
                  {b.firstName} {b.lastName}{b.nmls ? ` (NMLS: ${b.nmls})` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isBroker && processors.length > 0 && (
            <TextField
              select
              label="Processor"
              fullWidth
              size="small"
              value={selectedProcessorUserId}
              onChange={(e) => setSelectedProcessorUserId(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">None</MenuItem>
              {processors.map(p => (
                <MenuItem key={p.userId} value={p.userId}>
                  {p.firstName} {p.lastName}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            What would you like to do after importing?
          </Typography>
          <ButtonGroup fullWidth sx={{ mb: 2 }}>
            <Button
              variant={postAction === 'price' ? 'contained' : 'outlined'}
              startIcon={<CalculateIcon />}
              onClick={() => setPostAction('price')}
            >
              Price the Loan
            </Button>
            <Button
              variant={postAction === 'edit' ? 'contained' : 'outlined'}
              startIcon={<EditIcon />}
              onClick={() => setPostAction('edit')}
            >
              Edit the Loan
            </Button>
          </ButtonGroup>
          {postAction === 'price' && (
            <Card variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 1.5 }}>
                Pricing Filters
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    select label="Lock Term" fullWidth size="small"
                    value={lockDuration} onChange={(e) => setLockDuration(Number(e.target.value))}
                  >
                    {LOCK_DURATIONS.map(d => (
                      <MenuItem key={d} value={d}>{d} days</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    select label="Compensation" fullWidth size="small"
                    value={compensationType} onChange={(e) => setCompensationType(e.target.value)}
                  >
                    <MenuItem value="BPC">BPC</MenuItem>
                    <MenuItem value="LPC">LPC</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
              <Stack direction="row" flexWrap="wrap" spacing={0.5} useFlexGap sx={{ mb: 1 }}>
                {PRODUCT_FAMILIES.map(pf => (
                  <Button
                    key={pf.id} size="small"
                    variant={selectedFamilies.includes(pf.id) ? 'contained' : 'outlined'}
                    onClick={() => toggleFilter(selectedFamilies, setSelectedFamilies, pf.id)}
                  >
                    {pf.label}
                  </Button>
                ))}
              </Stack>
              <Stack direction="row" flexWrap="wrap" spacing={0.5} useFlexGap>
                {loanTermOptions.map(lt => (
                  <Button
                    key={lt} size="small"
                    variant={selectedTerms.includes(lt) ? 'contained' : 'outlined'}
                    onClick={() => toggleFilter(selectedTerms, setSelectedTerms, lt)}
                  >
                    {lt}yr
                  </Button>
                ))}
              </Stack>
            </Card>
          )}
          {postAction === 'edit' && (
            <Typography variant="body2" color="text.secondary">
              <InfoOutlinedIcon sx={{ fontSize: 14, mr: 0.5 }} />
              The loan will open in edit mode so you can review and update fields before proceeding.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" startIcon={<ArrowBackIcon />} onClick={() => setStep('confirm')} disabled={loading}>Back</Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <FileUploadIcon />}
            onClick={handleImportSubmit}
            disabled={loading}
          >
            {loading ? 'Importing...' : postAction === 'price' ? 'Import & Price Loan' : 'Import & Edit Loan'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (step === 'filters') {
    return (
      <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalTitleWithClose>New Loan — Pricing preferences</ModalTitleWithClose>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {fileChip}
          {isProcessor && (
            <TextField
              select
              label="Select Broker"
              required
              fullWidth
              size="small"
              value={selectedBrokerUserId}
              onChange={(e) => setSelectedBrokerUserId(e.target.value)}
              helperText="The broker who will originate this loan"
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Select a broker...</MenuItem>
              {allBrokers.map(b => (
                <MenuItem key={b.userId} value={b.userId}>
                  {b.firstName} {b.lastName}{b.nmls ? ` (NMLS: ${b.nmls})` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isBroker && processors.length > 0 && (
            <TextField
              select
              label="Processor"
              fullWidth
              size="small"
              value={selectedProcessorUserId}
              onChange={(e) => setSelectedProcessorUserId(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">None</MenuItem>
              {processors.map(p => (
                <MenuItem key={p.userId} value={p.userId}>
                  {p.firstName} {p.lastName}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We will analyze your MISMO file and recommend a program and rate before creating the loan.
          </Typography>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 1.5 }}>Pricing filters</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  select label="Lock Term" fullWidth size="small"
                  value={lockDuration} onChange={(e) => setLockDuration(Number(e.target.value))}
                >
                  {LOCK_DURATIONS.map(d => (
                    <MenuItem key={d} value={d}>{d} days</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  select label="Compensation" fullWidth size="small"
                  value={compensationType} onChange={(e) => setCompensationType(e.target.value)}
                >
                  <MenuItem value="BPC">BPC</MenuItem>
                  <MenuItem value="LPC">LPC</MenuItem>
                </TextField>
              </Grid>
              {compensationType === 'BPC' && (
                <>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      select label="Calc Method" fullWidth size="small"
                      value={bpcCalcMethod} onChange={(e) => { setBpcCalcMethod(e.target.value); setBpcValue(''); }}
                    >
                      <MenuItem value="PercentOfLoan">Percent</MenuItem>
                      <MenuItem value="FixedAmount">Fixed Amount</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    {bpcCalcMethod === 'FixedAmount' ? (
                      <CurrencyInput label="BPC $" value={bpcValue} onChange={setBpcValue} size="small" />
                    ) : (
                      <TextField
                        label="BPC %" type="number" fullWidth size="small"
                        value={bpcValue} onChange={(e) => setBpcValue(e.target.value)}
                        inputProps={{ step: 0.125, min: 0 }}
                      />
                    )}
                  </Grid>
                </>
              )}
            </Grid>
            <Stack direction="row" flexWrap="wrap" spacing={0.5} useFlexGap sx={{ mb: 1 }}>
              {PRODUCT_FAMILIES.map(pf => (
                <Button
                  key={pf.id} size="small"
                  variant={selectedFamilies.includes(pf.id) ? 'contained' : 'outlined'}
                  onClick={() => toggleFilter(selectedFamilies, setSelectedFamilies, pf.id)}
                >
                  {pf.label}
                </Button>
              ))}
            </Stack>
            <Stack direction="row" flexWrap="wrap" spacing={0.5} useFlexGap>
              {loanTermOptions.map(lt => (
                <Button
                  key={lt} size="small"
                  variant={selectedTerms.includes(lt) ? 'contained' : 'outlined'}
                  onClick={() => toggleFilter(selectedTerms, setSelectedTerms, lt)}
                >
                  {lt}yr
                </Button>
              ))}
            </Stack>
          </Card>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" startIcon={<ArrowBackIcon />} onClick={() => setStep('confirm')}>Back</Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            disabled={selectedFamilies.length === 0}
            onClick={() => { setError(null); setStep('advisor'); }}
          >
            Analyze & recommend
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!mismoImportAdvisorEnabled) {
    return null;
  }

  return (
    <Dialog open onClose={handleClose} maxWidth="md" fullWidth>
      <ModalTitleWithClose>New Loan — Your recommendation</ModalTitleWithClose>
      {error && (
        <Alert severity="error" sx={{ mx: 3, mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <MismoAdvisorStep
        selectedFile={selectedFile}
        lockDuration={lockDuration}
        compensationType={compensationType}
        selectedFamilies={selectedFamilies}
        selectedTerms={selectedTerms}
        bpcCalcMethod={bpcCalcMethod}
        bpcValue={bpcValue}
        onAgree={handleAdvisorAgree}
        onDisagree={handleAdvisorDisagree}
        onBack={() => setStep('filters')}
        loading={loading}
      />
    </Dialog>
  );
};

export default NewLoanModal;
